import React from "react";
import SingleSubjectScreening from "../SingleSubjectScreening";
import SingleSubjectSchedule from "../SingleSubjectSchedule";
import Timeline from "../../Timeline";
import SingleSubjectAE from "../SingleSubjectAE";
import SingleSubjectDosing from "../SingleSubjectDosing";
import ConcomitantMedications from "../../../Safety/ConcomitantMedications";
import ConcomitantProcedures from "../../../Safety/ConcomitantProcedures";
import Labs from "../../../Measures/Labs";
import Assessment from "../../../Measures/Assessment";
import AdministrativeFormRecords from "../../../Measures/AdministrativeFormRecords";
import Questionnaires from "../../../Measures/Questionnaires";
import SingleSubjectDeviations from "../SingleSubjectDeviations";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import AllDiscrepancies from "@/views/DataDiscrepancies/Discrepancies";
import Imaging from "@/views/Measures/Imaging";
import Pharmacokinetics from "@/views/Safety/Pharmacokinetics/Pharmacokinetics";
import SingleSubjectMissedDosing from "../SingleSubjectMissedDosing";
import SingleSubjectIPAdministration from "../SingleSubjectIPAdministration";
import IpDispensation from "../../../Safety/IpDispensation";
import NotesTab from "@/views/shared/NotesTab";
import ReviewContextTab from "@/views/MedicalReview/ReviewContextTab";

type MenuItem = {
  key: string;
  featureflag?: string | string[];
  label: React.ReactNode;
};

type MenuGroup = {
  type: "group";
  label: string;
  key: string;
  children: MenuItem[];
};

const filterItems = (
  groups: MenuGroup[],
  trialId: string,
  isFeatureFlagEnabled: (featureflag: string | string[]) => boolean,
): MenuGroup[] =>
  groups
    .map((group) => ({
      ...group,
      children: group.children.filter((item) => {
        if (item.key === "missed-dosing" && trialId === "DARATU-ALZ")
          return false;
        if (item.featureflag && !isFeatureFlagEnabled(item.featureflag))
          return false;
        return true;
      }),
    }))
    .filter((group) => group.children.length > 0);

export const createSidebarMenuItems = (
  trialId: string,
  isFeatureFlagEnabled: (featureflag: string | string[]) => boolean,
): MenuGroup[] => {
  const allGroups: MenuGroup[] = [
    {
      type: "group",
      label: "SUBJECT JOURNEY",
      key: "group-subject-journey",
      children: [
        {
          key: "schedule",
          featureflag: FEATURE_FLAGS.SCHEDULE,
          label: "Schedule",
        },
        {
          key: "screening",
          featureflag: FEATURE_FLAGS.SCREENING,
          label: "Screening",
        },
        {
          key: "timeline",
          featureflag: FEATURE_FLAGS.TIMELINE,
          label: "Timeline",
        },
      ],
    },
    {
      type: "group",
      label: "SAFETY",
      key: "group-safety",
      children: [
        {
          key: "medical-review",
          featureflag: FEATURE_FLAGS.MEDICAL_REVIEW,
          label: "Medical Review",
        },
        {
          key: "adverse-events",
          featureflag: FEATURE_FLAGS.ADVERSE_EVENTS,
          label: "Adverse Events",
        },
        {
          key: "concomitant-medications",
          featureflag: FEATURE_FLAGS.CONMEDS,
          label: "Con Medications",
        },
        {
          key: "concomitant-procedures",
          featureflag: FEATURE_FLAGS.CONCOMITANT_PROCEDURES,
          label: "Con Procedures",
        },
        {
          key: "ip-administration",
          featureflag: FEATURE_FLAGS.IP_ADMINISTRATION,
          label: "IP Administration",
        },
        {
          key: "ip-dispensation",
          featureflag: FEATURE_FLAGS.IP_DISPENSATION,
          label: "IP Dispensation",
        },
        {
          key: "missed-dosing",
          featureflag: FEATURE_FLAGS.IP_ADMINISTRATION,
          label: "Missed Doses",
        },
        {
          key: "pharmacokinetics",
          featureflag: FEATURE_FLAGS.PHARMACOKINETICS,
          label: "Pharmacokinetics",
        },
        {
          key: "study-intervention",
          featureflag: FEATURE_FLAGS.DOSING_TRACKER,
          label: "Study Intervention",
        },
      ],
    },
    {
      type: "group",
      label: "MEASURES",
      key: "group-measures",
      children: [
        {
          key: "administrative-form-records",
          featureflag: FEATURE_FLAGS.ADMINISTRATIVE_FORM_RECORDS,
          label: "Admin Form Records",
        },
        {
          key: "assessments",
          featureflag: FEATURE_FLAGS.SAFETY_MEASURES,
          label: "Assessments",
        },
        {
          key: "imaging",
          featureflag: FEATURE_FLAGS.IMAGING,
          label: "Imaging",
        },
        {
          key: "labs",
          featureflag: FEATURE_FLAGS.LABS,
          label: "Labs",
        },
        {
          key: "questionnaires",
          featureflag: FEATURE_FLAGS.EFFICACY_MEASURE,
          label: "Questionnaires",
        },
      ],
    },
    {
      type: "group",
      label: "DATA",
      key: "group-data",
      children: [
        {
          key: "data-discrepancies",
          featureflag: FEATURE_FLAGS.DATA_DISCREPANCIES,
          label: "Data Discrepancies",
        },
        {
          key: "protocol-deviations",
          featureflag: FEATURE_FLAGS.PROTOCOL_DEVIATIONS,
          label: "Protocol Deviations",
        },
        // Ungated, like the Notes tab on the site and investigator profiles:
        // a note is user-authored content, not a licensed data domain.
        { key: "notes", label: "Notes" },
      ],
    },
  ];

  return filterItems(allGroups, trialId, isFeatureFlagEnabled);
};

export const createContentMap = (
  subjectId: string,
): Record<string, React.ReactNode> => ({
  schedule: <SingleSubjectSchedule />,
  "medical-review": <ReviewContextTab subjectId={subjectId} />,
  screening: <SingleSubjectScreening />,
  "adverse-events": <SingleSubjectAE />,
  "ip-administration": <SingleSubjectIPAdministration />,
  "ip-dispensation": <IpDispensation singleSubjectId={subjectId} />,
  "missed-dosing": <SingleSubjectMissedDosing />,
  pharmacokinetics: <Pharmacokinetics singleSubjectId={subjectId} />,
  "study-intervention": <SingleSubjectDosing />,
  "concomitant-medications": (
    <ConcomitantMedications singleSubjectId={subjectId} />
  ),
  "concomitant-procedures": (
    <ConcomitantProcedures singleSubjectId={subjectId} />
  ),
  labs: <Labs singleSubjectId={subjectId} />,
  imaging: <Imaging singleSubjectId={subjectId} />,
  "administrative-form-records": (
    <AdministrativeFormRecords singleSubjectId={subjectId} />
  ),
  assessments: <Assessment singleSubjectId={subjectId} />,
  questionnaires: <Questionnaires singleSubjectId={subjectId} />,
  "data-discrepancies": <AllDiscrepancies singleSubjectId={subjectId} />,
  "protocol-deviations": <SingleSubjectDeviations />,
  timeline: <Timeline />,
  notes: <NotesTab entityKey={`subject:${subjectId}`} />,
});
